import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
import { annualAccountReportAccepted, annualAccountReportCommented } from "store/actions/annualAccountReportActions";
//#endregion
//#region types
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { ActionButton } from "models/propTypes/commonPropTypes";
import { AnnualAccountFooterProps } from "models/propTypes/annualAccountPropTypes";
//#endregion
//#region components
import { Icon, PageFooter, useCheckRelations } from "components/common";
import useAnnualAccountCommentDialog from "./forms/useAnnualAccountCommentDialog";
import useAnnualAccountOrderK10Dialog from "./forms/useAnnualAccountOrderK10Dialog";
import AnnualAccountMoreActions from "./moreActions";
import { useFeatureService } from "serviceHooks";
//#endregion
import { FeatureConstants, IconDefinitionConstants } from "utils/constants";
import { isCommentActionHidden } from "./isCommentActionHidden";
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const AnnualAccountFooter: React.FC<AnnualAccountFooterProps> = (props) => {
  const { annualAccountReport, markAsDoneHandler, submitForm, isLoadingDividendInformation, isK10OrderMode } = props;
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const { getIsRevisor, getIsAuditorAssistant } = useCheckRelations();
  const isRevisor = getIsRevisor() || getIsAuditorAssistant();
  const { hasCompanyFeature } = useFeatureService();
  const [isOptedIn, setIsOptedIn] = React.useState<boolean>();

  React.useEffect(() => {
    if (!isRevisor) return;
    hasCompanyFeature(FeatureConstants.AuditorCommunicationPanel)
      .then(setIsOptedIn)
      .catch(() => setIsOptedIn(false));
  }, [isRevisor]);

  const onCommentSuccess = () => {
    markAsDoneHandler && markAsDoneHandler();
    dispatch(annualAccountReportCommented(annualAccountReport.id));
  };

  const onApproveSuccess = () => {
    markAsDoneHandler && markAsDoneHandler();
    dispatch(annualAccountReportAccepted(annualAccountReport.id));
  };

  const getCommentDialog = useAnnualAccountCommentDialog(onCommentSuccess);
  const getOrderK10Dialog = useAnnualAccountOrderK10Dialog(onCommentSuccess);

  const openCommentDialog = async () => {
    if (annualAccountReport.financialYearId === undefined) return;
    dispatch(displayDialog(getCommentDialog(annualAccountReport.financialYearId)));
  };

  const openOrderK10Dialog = async () => {
    if (annualAccountReport.financialYearId === undefined) return;
    dispatch(displayDialog(getOrderK10Dialog(annualAccountReport.financialYearId)));
  };

  const commentAction: ActionButton = {
    text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Comment),
    onClick: openCommentDialog,
    type: StyleEnums.DisplayType.Info,
  };

  const approveAction: ActionButton = {
    text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Approve),
    onClick: () => submitForm(),
    type: StyleEnums.DisplayType.Success,
    successMessage: LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.AnnualAccount.AnnualReportAcceptSuccessMessage
    ),
    isDisabled: isLoadingDividendInformation || props.approveDisabled === true,
    onActionSuccess: onApproveSuccess,
  };

  const orderK10Action: ActionButton = {
    text: (
      <>
        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.K10OrderApproveButton)}
        <Icon className={cssExports.footerButtonMargingLeft} icon={IconDefinitionConstants.WChevronRight} />
      </>
    ),
    onClick: openOrderK10Dialog,
    type: StyleEnums.DisplayType.Info,
    isDisabled: isLoadingDividendInformation || props.approveDisabled === true,
  };

  const actions = isK10OrderMode
    ? [orderK10Action]
    : isCommentActionHidden(isRevisor, isOptedIn)
      ? [approveAction]
      : [commentAction, approveAction];

  return (
    <PageFooter
      allowForReadOnlyUsers
      actions={actions}
      moreActions={
        <AnnualAccountMoreActions annualAccountReport={annualAccountReport} markAsDoneHandler={markAsDoneHandler} />
      }
      showNavigateBack={props.showNavigateBack}
      disableSuccessMessages={props.disableSuccessMessages}
    />
  );
};

export default AnnualAccountFooter;
